
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { saveAuthToken } from "@/utils/storage";
import { BaseForm } from "@/components/auth/base-form";
import { NewPasswordForm } from "@/components/auth/new-password-form";
import { claimPlayer } from "@/utils/claim-player";
import { bindPendingGuardianInvites } from "@/utils/guardian-binding";
import { readRedirectParam, sanitizeRedirect } from "@/utils/auth-redirect";


const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isResetPassword, setIsResetPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getPostAuthDestination = async (): Promise<string> => {
    // 0. Attach any co-parent invitations addressed to this email
    await bindPendingGuardianInvites();

    // 1. Process claim intent if present
    const raw = sessionStorage.getItem("claimIntent");

    if (raw) {
      try {
        const intent = JSON.parse(raw);
        sessionStorage.removeItem("claimIntent");
        const result = await claimPlayer(intent.playerId, intent.teamId, "your child");
        if (result.success) {
          toast.success(result.message);
        } else if (!result.needsAuth) {
          toast.error(result.message);
        }
        if (intent.returnUrl) {
          try {
            const url = new URL(intent.returnUrl, window.location.origin);
            return url.pathname + url.search + url.hash;
          } catch {
            return intent.returnUrl;
          }
        }
      } catch {
        sessionStorage.removeItem("claimIntent");
      }
    }

    // 2. Check sessionStorage redirect (set by Google sign-in button)
    const paramRedirect = readRedirectParam();
    const savedRedirect = sanitizeRedirect(sessionStorage.getItem("postAuthRedirect"));
    sessionStorage.removeItem("postAuthRedirect");
    if (paramRedirect || savedRedirect) {
      return (paramRedirect || savedRedirect) as string;
    }

    // 3. Check if user is guardian-only → /parent
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: guardianRows } = await supabase
          .from("gameon_parent_guardians")
          .select("id")
          .eq("user_id", user.id)
          .in("status", ["active", "pending"])
          .limit(1);

        const { data: teamRows } = await supabase
          .from("teamvote_teams")
          .select("id")
          .eq("coach_id", user.id)
          .limit(1);

        if (guardianRows && guardianRows.length > 0 && (!teamRows || teamRows.length === 0)) {
          return "/parent";
        }
      }
    } catch (err) {
      console.error("Error checking user role:", err);
    }

    return "/teams";
  };

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("Auth callback page loaded");
        
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get("type");
        
        if (type === "recovery") {
          console.log("Password reset flow detected");
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log("User already logged in, showing password reset form");
          }
          setIsResetPassword(true);
          setIsLoading(false);
          return;
        }
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          setError("Authentication failed. Please try again.");
          setIsLoading(false);
          toast.error("Authentication failed", {
            description: sessionError.message
          });
          return;
        }
        
        if (session) {
          console.log("Session found in callback:", session);
          saveAuthToken(btoa(JSON.stringify(session.user)));

          try {
            const { pulse } = await import("@/utils/pulse");
            pulse("signin");
          } catch {}

          const destination = await getPostAuthDestination();
          console.log("Redirecting to:", destination);
          navigate(destination, { replace: true });
        } else {
          console.log("No session found in callback");
          setError("No session found. The link may have expired.");
          setIsLoading(false);
          toast.error("Authentication failed", {
            description: "No session found. The link may have expired."
          });
        }
      } catch (error: any) {
        console.error("Error in auth callback:", error);
        setError(error.message || "An error occurred during authentication");
        setIsLoading(false);
        toast.error("Authentication error", {
          description: error.message || "An error occurred during authentication"
        });
      }
    };
    
    handleAuthCallback();
  }, [navigate]);

  const handlePasswordResetSuccess = () => {
    toast.success("Password updated successfully");
    navigate("/teams", { replace: true });
  };

  if (isResetPassword) {
    return (
      <div className="max-container my-8">
        <div className="max-w-md mx-auto">
          <BaseForm title="Reset Your Password">
            <div className="p-4 border border-blue-200 bg-blue-50 rounded-md mb-4">
              <p className="text-sm text-blue-800">
                You're updating your password via reset link. Enter your new password below.
              </p>
            </div>
            <NewPasswordForm onSuccess={handlePasswordResetSuccess} />
          </BaseForm>
        </div>
      </div>
    );
  }

  return (
    <div className="max-container flex flex-col items-center justify-center min-h-[50vh]">
      <div className="bg-background/80 backdrop-blur p-8 rounded-lg border shadow-lg max-w-md w-full">
        <h1 className="text-2xl font-bold mb-4 text-center">
          {error ? "Authentication Error" : "Completing Sign In..."}
        </h1>
        
        {error ? (
          <div className="text-destructive text-center">
            <p>{error}</p>
            <button 
              onClick={() => navigate("/login")} 
              className="mt-4 bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 transition-colors"
            >
              Return to Login
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-4 text-center">Verifying your login, please wait...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
